import Person from "@mui/icons-material/Person";
import { Button } from "@mui/material";
import { useOmnisearchActions, useOmnisearchState } from "hooks/omnisearch-v2";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useEffect, useRef } from "react";

const CSM_KEY = "csm";
const ME = "me";

/** True when the omnisearch string carries an explicit `csm:` term. */
function hasCsmTerm(omnisearch: string): boolean {
  return /(^|\s)csm:/i.test(omnisearch);
}

/** True when the omnisearch string is scoped to csm:me. */
function isMineOn(omnisearch: string): boolean {
  return /(^|\s)csm:me(\s|$)/i.test(omnisearch);
}

/**
 * CSM-only "My findings" toggle. Scopes the grid + stat cards to the logged-in
 * CSM's tenants by adding/removing the `csm:me` omnisearch term (the resolver
 * expands it server-side). Sticky per user via localStorage: an explicit `csm`
 * term already in the URL (a shared link) always wins over the stored default.
 */
export function MyFindingsToggle() {
  const { user, isCsm } = useCurrentUser();
  const { editKeyValues, removeKey } = useOmnisearchActions();
  const { omnisearch } = useOmnisearchState();
  const [sticky, setSticky] = useLocalStorageState<boolean>(
    `cers-rejections-my-findings:${user?.id ?? "anon"}`,
    { defaultValue: false }
  );
  const appliedDefault = useRef(false);

  // Apply the sticky default once on mount, but never override an explicit URL term.
  // Guard: do nothing until the user id is resolved — prevents reading/applying
  // against the "anon" localStorage bucket if this effect fires before auth settles.
  useEffect(() => {
    if (!user?.id) return;
    if (appliedDefault.current) return;
    appliedDefault.current = true;
    if (sticky && !hasCsmTerm(omnisearch)) {
      editKeyValues([[CSM_KEY, ME]]);
    }
  }, [user?.id, sticky, omnisearch, editKeyValues]);

  if (!isCsm) return null;

  const on = isMineOn(omnisearch);
  const handleToggle = () => {
    if (on) {
      removeKey(CSM_KEY);
      setSticky(false);
    } else {
      editKeyValues([[CSM_KEY, ME]]);
      setSticky(true);
    }
  };

  // Contained-when-active / outlined-when-inactive matches the product's personal
  // scope-filter idiom (AssignedToMeButton / UnresolvedOnlyButton, Customer/Violations),
  // giving an unmistakable on/off state. Mounted in the grid command bar by index.tsx.
  return (
    <Button
      size="small"
      variant={on ? "contained" : "outlined"}
      aria-pressed={on}
      startIcon={<Person />}
      sx={{ px: 2 }}
      onClick={handleToggle}
      aria-label="My findings"
    >
      My Findings
    </Button>
  );
}
